import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EVENTS_ENDPOINT } from '@app/app.constants';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { PortalEventContext } from '@app/core/models/portal-event-context';
import { PortalDataService } from '@app/portal/services/portal-data.service';
import { debounce } from '@app/utils/common';
import { portalError } from '@app/utils/error/portal-error';
import { loggerFor } from '@app/utils/logger';
import { isNotBlank } from '@app/utils/string';
import { addSeconds } from 'date-fns';
import {
  BehaviorSubject,
  combineLatest,
  interval,
  Observable,
  Subject,
  Subscriber,
  Subscription,
  timer,
} from 'rxjs';
import { distinctUntilChanged, finalize, first, map, shareReplay, takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class EventSourceService {
  private _connecting$ = new BehaviorSubject<boolean>(false);

  get connecting$(): Observable<boolean> {
    return this._connecting$.asObservable();
  }

  private _isConnected$ = new BehaviorSubject(false);

  get isConnected$(): Observable<boolean> {
    return this._isConnected$.asObservable();
  }

  private _reconnectingIn$ = new BehaviorSubject<number | null>(null);

  get reconnectingIn$(): Observable<number | null> {
    return this._reconnectingIn$.asObservable();
  }

  private _subjects$ = new BehaviorSubject<PortalEventSubject[]>([]);

  get subjects$(): Observable<PortalEventSubject[]> {
    return this._subjects$.asObservable();
  }

  private get subjects(): PortalEventSubject[] {
    return Object.values(this.listeners)
      .filter(({ listeners }) => listeners.length > 0)
      .map(({ subject }) => subject);
  }

  private _state$ = combineLatest([this._connecting$, this._isConnected$, this._reconnectingIn$]).pipe(
    map(([connecting, isConnected, reconnectingIn]): EventSourceConnectionState => {
      if (isConnected) {
        return EventSourceConnectionState.Connected;
      } else if (reconnectingIn != null) {
        return EventSourceConnectionState.WaitingToReconnect;
      } else if (connecting) {
        return EventSourceConnectionState.Connecting;
      } else {
        return EventSourceConnectionState.Disconnected;
      }
    }),
    distinctUntilChanged()
  );

  readonly state$ = this._state$.pipe(shareReplay(1));

  private clientId: number | null = null;
  private eventDataSubject = new Subject<PortalEventData>();
  private eventSource: EventSource | null = null;
  private listeners: Record<string, Listener> = {};
  private log = loggerFor('EventSourceService');
  private reconnectTimeoutId: number | null = null;
  private reconnectSubscription: Subscription | null = null;

  /**
   * The event source is recreated everytime the restart subscription emits.
   */
  private restartSubscription: Subscription | null = null;

  private readonly debouncedUpdate = debounce(this.updateSubjects);
  private readonly reconnectTimeoutInSeconds = 10;
  private readonly healthCheckIntervalInSeconds = 35;

  constructor(
    private authService: AuthenticationService,
    private http: HttpClient,
    private portalDataService: PortalDataService
  ) {}

  addSubjects(...subjects: PortalEventSubject[]): Observable<PortalEventData> {
    this.log.debug('Adding new subjects', subjects.map(getSubjectLabel));

    return new Observable((subscriber) => {
      this.storeSubscriber(subjects, subscriber);
      this.debouncedUpdate(this.subjects);
      this._subjects$.next(this.subjects);

      const subscription = this.eventDataSubject.subscribe((value) => {
        const someSubjectMatches = subjects.some((subject) => this.subjectsMatch(subject, value.subject));

        if (someSubjectMatches) {
          subscriber.next(value);
        }
      });

      return () => {
        subscription.unsubscribe();
        this.removeSubscriber(subjects, subscriber);
      };
    });
  }

  initialize(): void {
    this.authService.isAuthenticated$.subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        this.log.info('User is authenticated, connecting.');
        this.connect();
      } else {
        this.log.info('Not authenticated, closing.');
        this.close();
      }
    });
  }

  observe(subject: Omit<PortalEventSubject, 'system'>): Observable<PortalEventData> {
    return new Observable((subscriber) => {
      const destroy$ = new Subject<void>();

      this.portalDataService.appInfo.data$.pipe(takeUntil(destroy$), first()).subscribe(({ system }) => {
        this.addSubjects({
          ...subject,
          system,
        })
          .pipe(takeUntil(destroy$))
          .subscribe((event) => subscriber.next(event));
      });

      return () => {
        destroy$.next();
        destroy$.complete();
      };
    });
  }

  restart(): void {
    this.log.info('Restarting EventSource');
    this.close();
    this.connect();
  }

  start(): void {
    this.log.info('Starting EventSource');
    this.connect();
  }

  stop(): void {
    this.log.info('Stopping EventSource');
    this.close();
  }

  stopReconnecting(): void {
    if (this.reconnectTimeoutId != null) {
      this.log.info('Stopping reconnect');

      window.clearTimeout(this.reconnectTimeoutId);
      this.reconnectSubscription?.unsubscribe();
      this._connecting$.next(false);
      this._isConnected$.next(false);
      this._reconnectingIn$.next(null);

      this.reconnectSubscription = null;
      this.reconnectTimeoutId = null;
    }
  }

  private close(): void {
    this._isConnected$.next(false);
    this.eventSource?.close();
    this.reconnectSubscription?.unsubscribe();
    this.restartSubscription?.unsubscribe();
    this.clientId = null;
    this.eventSource = null;
    this.reconnectSubscription = null;
    this.restartSubscription = null;

    if (this.reconnectTimeoutId != null) {
      window.clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
  }

  private connect() {
    if (this.eventSource != null) {
      this.eventSource.close();
      this.eventSource = null;
    }

    if (this.reconnectSubscription != null) {
      this.stopReconnecting();
    }

    if (this.authService.isAuthenticated === false) {
      return;
    }

    this._connecting$.next(true);
    this.eventSource = new EventSource(EVENTS_ENDPOINT, {
      withCredentials: true,
    });

    this.eventSource.addEventListener('open', () => {
      this.log.info('Established connection', new Date());
      this._connecting$.next(false);
      this._isConnected$.next(true);
      this.restartPeriodicHealthCheck(this.healthCheckIntervalInSeconds);
    });

    this.eventSource.addEventListener('error', (error) => {
      this.log.error('Event source errored', error);
      this.close();
      this.reconnect();
      // It can happen that event source will be closed due to expired user session.
      // The `error` contains no information provided by `portal-events` (`error` argument is in this regard useless).
      // Nevertheless, assuming that the stream was closed due to session expiry we should verify that with portal back-end.
      setTimeout(() => this.authService.requestUserSessionValidation().subscribe(), 2000);
    });

    this.eventSource.addEventListener('clientId', (event) => {
      this.log.info(`Received clientId. clientId=${event.data}`);
      this.clientId = event.data;
      this.debouncedUpdate(this.subjects ?? []);
    });

    this.eventSource.addEventListener('keepalive', () => {
      this.restartPeriodicHealthCheck(this.healthCheckIntervalInSeconds);
    });

    this.eventSource.addEventListener('message', (event) => {
      this.log.debug('Received message', event);
      this.eventDataSubject.next(this.parse(event.data));
      this.restartPeriodicHealthCheck(this.healthCheckIntervalInSeconds);
    });
  }

  private parse(data: string): PortalEventData {
    try {
      return JSON.parse(data);
    } catch {
      throw portalError({
        cause: new Error('Could not parse event data.', {
          cause: data,
        }),
        id: 'event-source:unparsable-message',
      });
    }
  }

  private reconnect() {
    this.log.info(`Event source will try to reconnect in ${this.reconnectTimeoutInSeconds} seconds`);

    this.reconnectSubscription = interval(1_000)
      .pipe(
        takeUntil(timer(addSeconds(new Date(), this.reconnectTimeoutInSeconds))),
        finalize(() => {
          this._reconnectingIn$.next(null);
        })
      )
      .subscribe((secondsPassed) => {
        this._reconnectingIn$.next(this.reconnectTimeoutInSeconds - secondsPassed - 1);
      });

    this.reconnectTimeoutId = window.setTimeout(() => {
      this.connect();
    }, this.reconnectTimeoutInSeconds * 1000);
  }

  private removeSubscriber(subjects: PortalEventSubject[], subscriber: Subscriber<PortalEventData>): void {
    this.log.debug('Remove subscriber. subjects=', subjects);

    subjects.forEach((subject) => {
      const key = getSubjectLabel(subject);

      if (this.listeners[key] != null) {
        this.listeners[key].listeners = this.listeners[key].listeners.filter(
          (listener) => listener !== subscriber
        );
      }
    });

    this._subjects$.next(this.subjects);
    this.debouncedUpdate(this.subjects);
  }

  private restartPeriodicHealthCheck(seconds: number) {
    if (this.restartSubscription != null) {
      this.restartSubscription.unsubscribe();
    }

    this.log.debug(
      `Starting timer to check if a message/keepalive message has been received in the last ${seconds}s`
    );

    this.restartSubscription = interval(seconds * 1000).subscribe(() => {
      this.log.info(`No message has been received in the last ${seconds}s. Restarting event source.`);
      this.restart();
    });
  }

  private storeSubscriber(subjects: PortalEventSubject[], subscriber: Subscriber<PortalEventData>): void {
    subjects.forEach((subject) => {
      const key = getSubjectLabel(subject);

      if (this.listeners[key] == null) {
        this.listeners[key] = {
          listeners: [],
          subject,
        };
      }

      this.listeners[key].listeners.push(subscriber);
    });
  }

  private updateSubjects(subjects: PortalEventSubject[]): void {
    if (this.eventSource != null) {
      this.http
        .post<string>(
          EVENTS_ENDPOINT,
          {
            clientId: this.clientId,
            subjects,
          },
          {
            observe: 'response',
          }
        )
        .subscribe({
          error: (error) => {
            this.log.error('Could not update subjects. Stopping and reconnecting.', error);
            this.stop();
            this.reconnect();
          },
          next: () => {
            this.log.info('Updated subjects', subjects.map(getSubjectLabel));
          },
        });
    }
  }

  private subjectsMatch(a: PortalEventSubject, b: PortalEventSubject): boolean {
    const matches = (a: any, b: any): boolean => (a == null && b == null) || a === b;
    const endUserMatches = matches(a.endUser, b.endUser);
    const enterpriseMatches = matches(a.enterprise, b.enterprise);
    const serviceProviderMatches = matches(a.serviceProvider, b.serviceProvider);
    const systemMatches = matches(a.system, b.system);
    const contextMatches = matches(a.context, b.context);

    return endUserMatches && enterpriseMatches && serviceProviderMatches && systemMatches && contextMatches;
  }
}

export interface PortalEventData {
  data: unknown;
  subject: PortalEventSubject;
}

export interface PortalEventSubject {
  system: string;
  serviceProvider?: string;
  enterprise?: string;
  endUser?: string;
  context: PortalEventContext;
}

export enum EventSourceConnectionState {
  Connected = 'Connected',
  Connecting = 'Connecting',
  Disconnected = 'Disconnected',
  WaitingToReconnect = 'WaitingToReconnect',
}

export function getSubjectLabel(subject: PortalEventSubject): string {
  return [subject.system, subject.serviceProvider, subject.enterprise, subject.endUser, subject.context]
    .filter(isNotBlank)
    .join('.');
}

interface Listener {
  listeners: Subscriber<PortalEventData>[];
  subject: PortalEventSubject;
}
